import {computed, DestroyRef, effect, inject, Injectable, signal} from "@angular/core";
import {
	AddStationToPeerPracticeSessionRequest,
	PeerPracticeSession,
	PeerPracticeStation,
	Scenario,
	SimUser,
	TrainingRoom,
	UpsertPeerPracticeSessionMetaDataRequest,
} from "@models";
import {GeneratedTypePredicates, SimEventService, SimGroupingService, SimSessionService} from "@services";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Translations} from "@translations";
import {DatePipe} from "@angular/common";
import {TranslateService} from "@ngx-translate/core";
import {debounceTime, filter} from "rxjs";
import {SimTimeService} from "../../../../../../core/services/sim-services/sim-time.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

// Form Types
export type PeerPracticeSession_StationForm = FormGroup<{
	trainingRoom: FormControl<TrainingRoom | undefined>;
	scenarios: FormControl<Scenario[] | undefined>;
}>;

@Injectable({
	providedIn: "root",
})
export class CreatePeerPracticeSessionDialogStateService {
	// Properties
	readonly #session = signal<PeerPracticeSession | null>(null);
	readonly #isLoading = signal<boolean>(true);
	readonly #eventId = signal<string>("");
	readonly #savePromise = signal<Promise<unknown> | undefined>(undefined);
	readonly #activeStationIndex = signal<number>(0);
	readonly #groupMembers = new Map<string, SimUser[]>();

	// Exposed Properties
	public readonly eventId = this.#eventId.asReadonly();
	public readonly isLoading = this.#isLoading.asReadonly();
	public readonly session = this.#session.asReadonly();
	public readonly savePromise = this.#savePromise.asReadonly();
	public readonly activeStationIndex = this.#activeStationIndex.asReadonly();
	public readonly stations = computed<PeerPracticeStation[]>(() => this.session()?.stations ?? []);
	public readonly intermissionOptions = signal<number[]>([5, 10, 15, 20, 30]).asReadonly();

	// Injections
	readonly #sessionService = inject(SimSessionService);
	readonly #formBuilder = inject(FormBuilder);
	readonly #datePipe = inject(DatePipe);
	readonly translate = inject(TranslateService);
	readonly #eventService = inject(SimEventService);
	readonly #groupService = inject(SimGroupingService);
	readonly #destroyRef = inject(DestroyRef);

	// Form
	public metadataForm = this.#formBuilder.group({
		title: new FormControl<string>("", Validators.required),
		date: new FormControl<Date>(new Date(), Validators.required),
		startTime: new FormControl<string>("", Validators.required),
		runDurationMinutes: new FormControl<number | null>(null, Validators.required),
		defaultIntermissionMinutes: new FormControl<number>(15, Validators.required),
	});
	public stationForms = new FormArray<PeerPracticeSession_StationForm>([]);

	public async initState(eventDay: Date | string, eventId: string, sessionId?: string): Promise<void> {
		this.#isLoading.set(true);
		this.#eventId.set(eventId);

		if (sessionId) {
			const session = (await this.#sessionService.getSessionById(sessionId)) as PeerPracticeSession;
			this.#session.set(session);
			this.populateForm();
		} else {
			this.metadataForm.patchValue({date: new Date(eventDay)}, {emitEvent: false});
		}
		this.handleValueChanges();

		this.#isLoading.set(false);
	}

	public addStationForm(value?: {trainingRoom: TrainingRoom; scenarios: Scenario[]}): void {
		const stationForm: PeerPracticeSession_StationForm = this.#formBuilder.group({
			trainingRoom: new FormControl<TrainingRoom | undefined>(undefined, Validators.required),
			scenarios: new FormControl<Scenario[] | undefined>([], Validators.required),
		}) as PeerPracticeSession_StationForm;
		if (value) {
			stationForm.setValue(value);
		}
		this.stationForms.push(stationForm, {emitEvent: false});
	}

	public deleteStation(index: number): void {
		const sessionId = this.#session()?.id;
		const stationId = this.#session()?.stations[index]?.id;
		if (stationId && sessionId) {
			this.#sessionService.removeStationFromPeerPracticeSession({stationId, sessionId}).then((session) => {
				this.#session.set(session);
				this.stationForms.removeAt(index);
			});
		} else {
			this.stationForms.removeAt(index);
		}
	}

	public setCurrentStationIndex(index: number): void {
		this.#activeStationIndex.set(index);
	}

	public addUserGroupById(groupId: string): void {
		const sessionId = this.#session()?.id;
		const isGroupAssigned = this.#session()?.userSubGroups.some((userSubGroup) => userSubGroup.id === groupId);
		if (!sessionId || isGroupAssigned) {
			return;
		}

		this.#savePromise.set(
			this.#sessionService.addGroupToPeerPracticeSession(sessionId, groupId).then((session) => {
				this.#session.set({...session});
			}),
		);
	}

	public async removeUserGroupById(groupId: string): Promise<void> {
		// Validate
		const sessionId = this.#session()?.id;
		const isGroupAssigned = this.#session()?.userSubGroups.some((userSubGroup) => userSubGroup.id === groupId);
		if (!sessionId || !isGroupAssigned) {
			return;
		}

		// Execute
		const promise = this.#sessionService.removeGroupFromPeerPracticeSession(sessionId, groupId);
		this.#savePromise.set(promise);
		const session = await promise;
		this.#session.set({...session});
		this.#groupMembers.delete(groupId);
	}

	public async getGroupMembersByGroupId(groupId: string): Promise<SimUser[]> {
		const isCached = this.#groupMembers.has(groupId);
		if (isCached) {
			return this.#groupMembers.get(groupId) ?? [];
		} else {
			const members = await this.#groupService.getGroupMembersByGroupIds({simGroupIds: [groupId]});
			const userMembers = members.filter((member) => GeneratedTypePredicates.isSimUser(member)) as SimUser[];
			this.#groupMembers.set(groupId, userMembers);
			return userMembers;
		}
	}

	public async changeIntermission(intermissionMinutes: number, scheduleEntryId: string): Promise<void> {
		const sessionId = this.#session()?.id;
		if (!sessionId) {
			return;
		}
		const promise = this.#sessionService.updateIntermissionInPeerPracticeSession(sessionId, scheduleEntryId, {
			duration: SimTimeService.fromMinutesToMilliseconds(intermissionMinutes),
		});
		this.#savePromise.set(promise);
		const session = await promise;
		this.#session.set(session);
	}

	private populateForm(): void {
		const session = this.session();
		if (!session) {
			return;
		}

		// Step 1
		this.metadataForm.setValue(
			{
				title: session.title,
				date: new Date(session.startTime),
				startTime: this.#datePipe.transform(new Date(session.startTime), this.translate.instant(Translations.DATE_FORMAT_TIME)),
				runDurationMinutes: SimTimeService.fromMillisecondsToMinutes(session.runDuration),
				defaultIntermissionMinutes: SimTimeService.fromMillisecondsToMinutes(session.defaultBreakDuration),
			},
			{emitEvent: false},
		);
		session.stations.forEach((station) =>
			this.addStationForm({
				trainingRoom: station.room ?? ({id: station.roomId} as TrainingRoom),
				scenarios: station.scenarios ?? [],
			}),
		);
	}

	private handleValueChanges(): void {
		this.metadataForm.valueChanges
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				filter(() => this.metadataForm.valid),
				debounceTime(500),
			)
			.subscribe(() => this.saveMetadata());

		this.stationForms.valueChanges
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				filter(() => this.stationForms.at(this.activeStationIndex())?.valid),
				debounceTime(500),
			)
			.subscribe(() => this.saveStation());
	}

	private saveStation(): void {
		// Get station data
		const value = this.stationForms.at(this.activeStationIndex()).getRawValue();
		const stationId = this.session()?.stations[this.activeStationIndex()]?.id;

		// Create request
		const request = {
			sessionId: this.#session()?.id,
			stationId: stationId,
			scenarioIds: value.scenarios?.map((scenario) => scenario.id),
			roomId: value.trainingRoom?.id,
		} as AddStationToPeerPracticeSessionRequest;

		// Send request
		const savePromise = stationId
			? this.#sessionService.updateStationInPeerPracticeSession({stationId, ...request})
			: this.#sessionService.addStationToPeerPracticeSession(request);
		this.#savePromise.set(savePromise);

		// Handle response
		savePromise.then((session: PeerPracticeSession) => {
			this.#session.set({...session});
		});
	}

	private async saveMetadata(): Promise<void> {
		// Validate
		const value = this.metadataForm.getRawValue();
		if (!value.date || !value.startTime || !value.title || !value.runDurationMinutes || !value.defaultIntermissionMinutes) {
			console.error("Invalid metadata form value");
			return;
		}

		// Create request
		const request: UpsertPeerPracticeSessionMetaDataRequest = {
			runDuration: SimTimeService.fromMinutesToMilliseconds(value.runDurationMinutes),
			sessionId: this.#session()?.id,
			startTime: SimTimeService.addTimeStringToDate(value.date, value.startTime),
			eventId: this.#eventId(),
			title: value.title,
		};
		const promise = this.#sessionService.upsertPeerPracticeSessionMetaData(request);
		this.#savePromise.set(promise);
		const session = await promise;

		// If new; assign session to event
		if (!this.#session()?.id) {
			await this.#eventService.addSessionToEvent(this.#eventId(), session.id);
		}

		// Update state
		this.#session.set(session);
	}

	readonly #validateActiveIndex = effect(() => {
		const activeIndex = this.activeStationIndex();
		const lastIndex = this.stationForms.length - 1;
		if (activeIndex > lastIndex) {
			this.#activeStationIndex.set(lastIndex);
		}
	});
}
